<template>
  <section
    class="accruals-im-groups"
    :class="{
      'accruals-im-groups--view': !editing,
      'accruals-im-groups--embedded': embedded,
    }"
    data-testid="accruals-configuration-page__im-groups-builder"
  >
    <header class="accruals-im-groups__header">
      <h2 class="accruals-im-groups__title">Conditions</h2>
      <base-button
        v-if="editing"
        secondary
        small
        label="+ Add condition group"
        class="accruals-im-groups__add-group-btn"
        :disabled="saving"
        data-testid="accruals-configuration-page__add-group-btn"
        @click="$emit('add-group')"
      />
    </header>

    <div
      v-for="(group, groupIdx) in groups"
      :key="group.id"
      class="accruals-im-groups__group-wrap"
    >
      <article
        class="accruals-im-groups__group"
        :data-testid="`accruals-configuration-page__im-group--${groupIdx}`"
      >
        <header class="accruals-im-groups__group-header">
          <div class="accruals-im-groups__group-title">
            <span class="accruals-im-groups__group-index">{{ groupIdx + 1 }}</span>
            <p v-if="editing" class="accruals-im-groups__group-label">When these properties:</p>
          </div>
          <div v-if="editing" class="accruals-im-groups__group-header-actions">
            <div class="accruals-im-groups__match-radios">
              <label class="accruals-im-groups__radio">
                <input
                  type="radio"
                  :name="`group-logic-${group.id}`"
                  value="OR"
                  :checked="group.logic === 'OR'"
                  :data-testid="`accruals-configuration-page__im-group-any--${groupIdx}`"
                  @change="$emit('set-group-logic', { groupId: group.id, logic: 'OR' })"
                />
                <span>Match ANY of the below</span>
              </label>
              <label class="accruals-im-groups__radio">
                <input
                  type="radio"
                  :name="`group-logic-${group.id}`"
                  value="AND"
                  :checked="group.logic !== 'OR'"
                  :data-testid="`accruals-configuration-page__im-group-all--${groupIdx}`"
                  @change="$emit('set-group-logic', { groupId: group.id, logic: 'AND' })"
                />
                <span>Match ALL of the below</span>
              </label>
            </div>
            <base-button
              v-if="canRemoveGroup"
              icon-style
              small
              class="accruals-im-groups__remove-group-btn"
              :image="assets.ICON_DELETE"
              image-alt="Remove condition group"
              :disabled="saving"
              aria-label="Remove condition group"
              :data-testid="`accruals-configuration-page__im-remove-group--${groupIdx}`"
              @click="$emit('remove-group', group.id)"
            />
          </div>
        </header>

        <div class="accruals-im-groups__conditions">
          <div
            v-for="(condition, condIdx) in group.conditions"
            :key="condition.id"
            class="accruals-im-groups__condition-block"
          >
            <div
              v-if="condIdx > 0"
              class="accruals-im-groups__cond-divider"
            >
              <span class="accruals-im-groups__cond-divider-line" />
              <span class="accruals-im-groups__cond-pill">{{ group.logic === 'OR' ? 'OR' : 'AND' }}</span>
              <span class="accruals-im-groups__cond-divider-line" />
            </div>
            <accruals-condition-row
              :condition="condition"
              :index="condIdx"
              :field-items="resolvedFieldItems"
              :editing="editing"
              :inline-edit="true"
              :show-delete="editing"
              :saving="saving"
              :row-actions="false"
              :show-index="false"
              :errors="conditionErrors[`${group.id}:${condIdx}`] || emptyErrors"
              @update="onUpdateCondition(group.id, condIdx, $event)"
              @delete="$emit('delete-condition', { groupId: group.id, index: condIdx })"
            />
          </div>
        </div>

        <footer v-if="editing" class="accruals-im-groups__group-footer">
          <div class="accruals-im-groups__group-footer-divider" />
          <button
            type="button"
            class="accruals-im-groups__add-condition"
            :disabled="saving"
            :data-testid="`accruals-configuration-page__im-add-condition--${groupIdx}`"
            @click="$emit('add-condition', group.id)"
          >
            + Add new condition
          </button>
        </footer>
      </article>

      <div
        v-if="groupIdx < groups.length - 1"
        class="accruals-im-groups__between"
        :data-testid="`accruals-configuration-page__im-group-connector--${groupIdx}`"
      >
        <div class="accruals-im-groups__between-line" />
        <div
          class="accruals-im-groups__between-toggle"
          :class="{ 'accruals-im-groups__between-toggle--readonly': !editing }"
        >
          <button
            type="button"
            :class="[
              'accruals-im-groups__logic-btn',
              connectorAt(groupIdx) === 'AND' ? 'accruals-im-groups__logic-btn--active' : '',
            ]"
            :disabled="!editing"
            :data-testid="`accruals-configuration-page__im-group-logic-and--${groupIdx}`"
            @click="$emit('set-group-connector', { index: groupIdx, logic: 'AND' })"
          >
            AND
          </button>
          <button
            type="button"
            :class="[
              'accruals-im-groups__logic-btn',
              connectorAt(groupIdx) === 'OR' ? 'accruals-im-groups__logic-btn--active' : '',
            ]"
            :disabled="!editing"
            :data-testid="`accruals-configuration-page__im-group-logic-or--${groupIdx}`"
            @click="$emit('set-group-connector', { index: groupIdx, logic: 'OR' })"
          >
            OR
          </button>
        </div>
        <div class="accruals-im-groups__between-line" />
      </div>
    </div>
  </section>
</template>

<script>
import AccrualsConditionRow from './AccrualsConditionRow.vue';
import {
  ACCRUALS_CONFIG_ASSETS,
  INFO_REQUEST_CONDITION_FIELDS,
} from '../accrualsConfigurationConstants';

export default {
  name: 'AccrualsImGroupsBuilder',
  components: { AccrualsConditionRow },
  props: {
    groups: { type: Array, default: () => [] },
    groupLogic: { type: String, default: 'AND' },
    groupConnectors: { type: Array, default: () => [] },
    editing: { type: Boolean, default: true },
    embedded: { type: Boolean, default: false },
    editingGroupId: { type: String, default: '' },
    editingIndex: { type: Number, default: -1 },
    saving: { type: Boolean, default: false },
    conditionErrors: { type: Object, default: () => ({}) },
    fieldItemsOverride: { type: Array, default: () => [] },
  },
  data() {
    return {
      assets: ACCRUALS_CONFIG_ASSETS,
      emptyErrors: { field: '', operator: '', value: '' },
    };
  },
  computed: {
    resolvedFieldItems() {
      if (this.fieldItemsOverride && this.fieldItemsOverride.length) {
        return this.fieldItemsOverride;
      }
      return INFO_REQUEST_CONDITION_FIELDS;
    },
    canRemoveGroup() {
      return this.groups.length > 1;
    },
  },
  methods: {
    connectorAt(index) {
      const raw = this.groupConnectors[index];
      if (raw === 'OR' || raw === 'AND') return raw;
      return this.groupLogic === 'OR' ? 'OR' : 'AND';
    },
    onUpdateCondition(groupId, index, condition) {
      this.$emit('update-condition', { groupId, index, condition });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsImGroupsBuilder.scss';
</style>
